/**
 * Severity configuration — Single Source of Truth (MGM-08 / SYS-36, TES-1365).
 * =============================================================================
 *
 * Owner decision 2026-06-14 (Option A): the real `defect_severity` enum
 * (`low | medium | high | critical`, see `src/integrations/supabase/types.ts`)
 * stays as-is — NO new DB table, no new enum value. Everything that used to be
 * duplicated across ~8 components (level ordering, urgency rank, the
 * `severity-*` design-token classes, the filled badge-chip mapping, the i18n
 * label/caption keys) is consolidated here so a single edit propagates.
 *
 * TES-1692 (Owner Thomsen 2026-06-19): the binding 3-level catalog A/B/C
 * (A=critical/PRIO 1, B=high/PRIO 2 = default, C=medium/PRIO 3) is layered on
 * top of the same enum here — `code` + `prio` + the `defects.severity.catalog.*`
 * labels. `low` stays in the enum but is no longer offered (see ACTIVE_SEVERITIES).
 * Still no new DB table; the real schema part is the column default (migration 317).
 *
 * Tailwind note: every class string below is a **literal** — the JIT content
 * scanner must see `bg-severity-low` etc. verbatim in source or it purges them.
 * Never build these via template interpolation (`bg-severity-${value}`).
 */
import type { DefectSeverity } from "@/types";

/** Per-surface design-token classes for one severity level. */
export interface SeverityClasses {
  /** Solid status-dot fill — `bg-severity-*` (SeveritySelectorCards). */
  dot: string;
  /** Foreground text colour — `text-severity-*` (severity text surfaces, SoT public API). */
  text: string;
  /** Soft tinted chip (bg `/15` + text) — InspectionPreview. */
  softChip: string;
  /** Filled container badge-chip — SeverityBadge (semantic container tokens,
   *  intentionally NOT the uniform `severity-*` tokens; high/critical share). */
  badge: string;
}

/** Full config for one severity level. */
export interface SeverityLevelConfig {
  /** Canonical enum value. */
  value: DefectSeverity;
  /**
   * A/B/C-Katalog-Code (TES-1692, Owner Thomsen 2026-06-19): A=critical, B=high, C=medium.
   * `null` = nicht im verbindlichen A/B/C-Katalog (gilt für `low`, erhalten aber nicht angeboten).
   */
  code: "A" | "B" | "C" | null;
  /** PRIO (TES-1692): 1=A, 2=B, 3=C; `null` für `low`. Niedriger = höhere Priorität. */
  prio: number | null;
  /** Urgency rank — higher = more urgent. Drives inbox sorting. */
  rank: number;
  /** i18n key for the human label (`defects.severity.<value>`). */
  labelKey: string;
  /** i18n key for the full A/B/C catalog label (`defects.severity.catalog.<value>`). */
  catalogLabelKey: string;
  /** i18n key for the screen-03 caption (`defects.severity.caption.<value>`). */
  captionKey: string;
  /** i18n key for the dialog-form label (`defects.dialog.severity<Capitalized>`). */
  dialogLabelKey: string;
  /** Tailwind design-token classes per render surface. */
  classes: SeverityClasses;
  /** Whether this level pulses its dot (screen-03: critical only). */
  pulse: boolean;
}

/**
 * The authoritative per-level config. Insertion order is ascending urgency,
 * which `SEVERITY_LEVELS` re-derives explicitly from `rank`.
 *
 * Intentionally module-private: production consumers go through the derived
 * public surface (`getSeverityConfig`, `SEVERITY_LEVELS`, `ACTIVE_SEVERITIES`,
 * the `severity*Class` maps, `buildSeverityCatalogLabels`) — exporting the raw
 * record would be an unused export (no production consumer imports it directly).
 */
const SEVERITY_CONFIG: Record<DefectSeverity, SeverityLevelConfig> = {
  low: {
    value: "low",
    code: null,
    prio: null,
    rank: 0,
    labelKey: "defects.severity.low",
    catalogLabelKey: "defects.severity.catalog.low",
    captionKey: "defects.severity.caption.low",
    dialogLabelKey: "defects.dialog.severityLow",
    classes: {
      dot: "bg-severity-low",
      text: "text-severity-low",
      softChip: "bg-severity-low/15 text-severity-low",
      badge: "border-transparent bg-secondary text-secondary-foreground",
    },
    pulse: false,
  },
  medium: {
    value: "medium",
    code: "C",
    prio: 3,
    rank: 1,
    labelKey: "defects.severity.medium",
    catalogLabelKey: "defects.severity.catalog.medium",
    captionKey: "defects.severity.caption.medium",
    dialogLabelKey: "defects.dialog.severityMedium",
    classes: {
      dot: "bg-severity-medium",
      text: "text-severity-medium",
      softChip: "bg-severity-medium/15 text-severity-medium",
      badge: "border-transparent bg-tertiary-container text-tertiary-container-foreground",
    },
    pulse: false,
  },
  high: {
    value: "high",
    code: "B",
    prio: 2,
    rank: 2,
    labelKey: "defects.severity.high",
    catalogLabelKey: "defects.severity.catalog.high",
    captionKey: "defects.severity.caption.high",
    dialogLabelKey: "defects.dialog.severityHigh",
    classes: {
      dot: "bg-severity-high",
      text: "text-severity-high",
      softChip: "bg-severity-high/15 text-severity-high",
      badge: "border-transparent bg-destructive-container text-destructive-container-foreground",
    },
    pulse: false,
  },
  critical: {
    value: "critical",
    code: "A",
    prio: 1,
    rank: 3,
    labelKey: "defects.severity.critical",
    catalogLabelKey: "defects.severity.catalog.critical",
    captionKey: "defects.severity.caption.critical",
    dialogLabelKey: "defects.dialog.severityCritical",
    classes: {
      dot: "bg-severity-critical",
      text: "text-severity-critical",
      softChip: "bg-severity-critical/15 text-severity-critical",
      badge: "border-transparent bg-destructive-container text-destructive-container-foreground",
    },
    pulse: true,
  },
};

/** Canonical display order — ascending urgency (low → critical). */
export const SEVERITY_LEVELS: DefectSeverity[] = (
  Object.values(SEVERITY_CONFIG) as SeverityLevelConfig[]
)
  .sort((a, b) => a.rank - b.rank)
  .map((c) => c.value);

/**
 * Default-Schweregrad B (Hauptfehler) — TES-1692, Owner Thomsen 2026-06-19. Vorbelegung in
 * Erfassung & Prüfung; entspricht dem DB-Spalten-Default aus Migration 317. Mappt auf `high`.
 */
export const SEVERITY_DEFAULT: DefectSeverity = "high";

/**
 * Die im verbindlichen A/B/C-Katalog angebotenen Schweregrade in Katalog-Reihenfolge
 * A → B → C (= critical, high, medium), sortiert über PRIO (TES-1692). `low` ist bewusst
 * NICHT enthalten (Enum-Wert bleibt erhalten, wird aber nicht mehr angeboten).
 */
export const ACTIVE_SEVERITIES: DefectSeverity[] = (
  Object.values(SEVERITY_CONFIG) as SeverityLevelConfig[]
)
  .filter((c) => c.code !== null)
  .sort((a, b) => (a.prio ?? Number.MAX_SAFE_INTEGER) - (b.prio ?? Number.MAX_SAFE_INTEGER))
  .map((c) => c.value);

/** Look up the full config for a level. */
export function getSeverityConfig(severity: DefectSeverity): SeverityLevelConfig {
  return SEVERITY_CONFIG[severity];
}

/**
 * Urgency rank (higher = more urgent). Accepts a raw `string` because callers
 * sort DB-sourced rows whose `severity` is typed loosely; an unknown value
 * yields `-1` so it sorts to the end.
 */
export function severityRank(severity: string): number {
  return SEVERITY_CONFIG[severity as DefectSeverity]?.rank ?? -1;
}

function classMap(key: keyof SeverityClasses): Record<DefectSeverity, string> {
  return SEVERITY_LEVELS.reduce(
    (acc, level) => {
      acc[level] = SEVERITY_CONFIG[level].classes[key];
      return acc;
    },
    {} as Record<DefectSeverity, string>,
  );
}

/** `bg-severity-*` dot fills, keyed by level. */
export const severityDotClass: Record<DefectSeverity, string> = classMap("dot");
/** Soft tinted chip classes, keyed by level. */
export const severitySoftChipClass: Record<DefectSeverity, string> = classMap("softChip");
/** Filled container badge-chip classes, keyed by level. */
export const severityBadgeClass: Record<DefectSeverity, string> = classMap("badge");

// `severityTextClass` (text-severity-* map) and `buildSeverityDialogLabels`
// (defects.dialog.severity* label builder) were removed with TES-1722: their last
// consumer (DefectFormFields) is gone now that severity renders via
// SeveritySelectorCards in BOTH create and edit. The underlying `text` / dialog-label
// config fields stay as SoT data so a future surface can re-derive them.
